"use client";

import Link, { type LinkProps } from "@/infrastructure/router/Link";
import type { Route } from "@/infrastructure/router/types";
import { useEffect, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	ArtworkThumbnail,
	EmptyState,
	GlassTabPill,
	Skeleton,
} from "@/components/design-system/ArtistDesignSystem";
import { SearchExperience } from "@/components/discovery/SearchExperience";
import {
	browserDiscoveryRepository,
	type DiscoveryRepository,
	type DiscoverySnapshot,
	type DiscoveryTrack,
	type Inspiration,
	playbackHref,
} from "@/domain/discovery/DiscoveryRepository";
import {
	beginnerTutorialStore,
	useBeginnerTutorial,
} from "@/domain/tutorial/BeginnerTutorial";
import { useTranslation, type TranslationKey } from "@/i18n/ArtistI18n";

type HomeTab = "discover" | "inspiration" | "catalog";

const HOME_TABS: readonly HomeTab[] = ["discover", "inspiration", "catalog"];
const HOME_TAB_LABEL_KEY: Record<HomeTab, TranslationKey> = {
	discover: "discovery.tabs.discover",
	inspiration: "discovery.tabs.inspiration",
	catalog: "discovery.tabs.catalog",
};

type HomeTone = DiscoveryTrack["artwork"];
const TONE_CLASS: Record<HomeTone, string> = {
	violet: "home-tone--violet",
	amber: "home-tone--amber",
	blue: "home-tone--blue",
	lime: "home-tone--lime",
	rose: "home-tone--rose",
};

function paletteClass(index: number): string {
	return `home-palette--${index % 5}`;
}

const INSPIRATION_SKELETONS: readonly { readonly id: string; readonly height: number }[] = Array.from(
	{ length: 6 },
	(_, index) => ({ id: `inspiration-skeleton-${index}`, height: 240 + (index % 3) * 40 }),
);

function PlayCircleFilledGlyph({ size = 28 }: Readonly<{ size?: number }>) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
			<circle cx="12" cy="12" r="11" fill="rgb(0 0 0 / 0.42)" />
			<path d="m10 8 6 4-6 4Z" fill="rgb(255 255 255)" />
		</svg>
	);
}

function PlayCircleGlyph({ size = 24 }: Readonly<{ size?: number }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			aria-hidden="true"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.6}
		>
			<circle cx="12" cy="12" r="10" />
			<path d="m10 8 6 4-6 4Z" fill="currentColor" stroke="none" />
		</svg>
	);
}

function WarningGlyph() {
	return (
		<svg
			width={38}
			height={38}
			viewBox="0 0 24 24"
			aria-hidden="true"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.6}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M12 3 2 21h20L12 3Z" />
			<path d="M12 10v5" />
			<path d="M12 18.5h.01" />
		</svg>
	);
}

/**
 * DiscoveryHub — Web home surface matching the iOS `HomeView` layout.
 *
 * Top toolbar (leading avatar → principal segmented control 发现/灵感/目录 →
 * trailing search) mirrors the iOS `toolbar` placement. The discover tab
 * renders the four iOS sections (hottest chart, hot circles, last listened,
 * your works) as semantic `<section>` / `<ul>` / `<article>` nodes. The
 * catalog tab embeds `SearchExperience` (web adaptation of Apple Music feed).
 */
export function DiscoveryHub({
	repository = browserDiscoveryRepository,
}: Readonly<{ repository?: DiscoveryRepository }>) {
	const { t } = useTranslation();
	const auth = useArtistSession();
	const tutorial = useBeginnerTutorial();
	const [tab, setTab] = useState<HomeTab>("discover");
	const [snapshot, setSnapshot] = useState<DiscoverySnapshot | null>(null);
	const [inspirations, setInspirations] = useState<
		readonly Inspiration[] | null
	>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [reload, setReload] = useState(0);
	const [refreshing, setRefreshing] = useState(false);

	useEffect(() => {
		void reload;
		let active = true;
		setError(null);
		Promise.all([
			repository.loadHome(auth.scope),
			repository.loadInspirations(auth.scope),
		])
			.then(([home, ideas]) => {
				if (active) {
					setSnapshot(home);
					setInspirations(ideas);
				}
			})
			.catch(() => {
				if (active) setError(t("discovery.error.loadFailed"));
			});
		return () => {
			active = false;
		};
	}, [auth.scope, reload, repository, t]);

	async function refresh() {
		setRefreshing(true);
		setReload((value) => value + 1);
		setRefreshing(false);
	}

	return (
		<div className="discovery-hub">
			<header className="home-toolbar">
				<div className="home-toolbar__leading">
					<Link
						href="/account"
						className="ds-icon-button home-toolbar__avatar"
						aria-label={t("discovery.account")}
					>
						<ArtistIcon name="person" size={20} />
					</Link>
				</div>
				<div className="home-toolbar__principal">
					<GlassTabPill<HomeTab>
						tabs={HOME_TABS}
						selected={tab}
						onSelect={setTab}
						label={(value) => t(HOME_TAB_LABEL_KEY[value])}
					/>
				</div>
				<div className="home-toolbar__trailing">
					<Link href="/search" className="ds-icon-button" aria-label={t("discovery.search")}>
						<ArtistIcon name="search" size={22} />
					</Link>
				</div>
			</header>

			{refreshing ? (
				<div className="home-ptr" role="status">
					<ArtistIcon name="sparkle" size={16} />
					<span>{t("discovery.notice.refreshing")}</span>
				</div>
			) : null}

			{error !== null ? (
				<EmptyState
					title={t("discovery.error.title")}
					message={error}
					symbol={<WarningGlyph />}
					actionLabel={t("common.retry")}
					actionSymbol={<ArtistIcon name="sparkle" size={16} />}
					onAction={() => setReload((value) => value + 1)}
				/>
			) : null}

			{notice !== null ? (
				<div className="capability-gate discovery-notice" role="status">
					<strong>{t("discovery.playbackBlocked.title")}</strong>
					<p>{notice}</p>
					<button type="button" onClick={() => setNotice(null)}>
						{t("common.ok")}
					</button>
				</div>
			) : null}

			<section role="tabpanel" hidden={tab !== "discover"} aria-label={t("discovery.tabs.discover")}>
				{!tutorial.completed && !tutorial.sessionBannerDismissed ? (
					<div
						style={{
							margin: "12px 16px 4px",
							padding: "14px",
							borderRadius: "20px",
							display: "flex",
							alignItems: "center",
							gap: "12px",
							background: "linear-gradient(120deg, var(--ds-surface), color-mix(in srgb, var(--ds-accent) 10%, var(--ds-surface)))",
							border: "1px solid color-mix(in srgb, var(--ds-accent) 24%, var(--ds-divider))",
						}}
					>
						<Link
							href="/genart"
							style={{ display: "flex", flex: 1, alignItems: "center", gap: "12px", color: "inherit", textDecoration: "none" }}
						>
							<span style={{ width: "44px", height: "44px", borderRadius: "50%", display: "grid", placeItems: "center", color: "var(--ds-accent)", background: "color-mix(in srgb, var(--ds-accent) 18%, transparent)" }}>
								<ArtistIcon name="sparkle" size={22} />
							</span>
							<span style={{ flex: 1 }}>
								<strong style={{ display: "block" }}>{t("discovery.tutorial.title")}</strong>
								<small style={{ color: "var(--ds-text-secondary)" }}>{t("discovery.tutorial.message")}</small>
							</span>
							<ArtistIcon name="chevron" size={20} />
						</Link>
						<button
							type="button"
							className="ds-icon-button"
							aria-label={t("discovery.tutorial.dismiss")}
							onClick={beginnerTutorialStore.dismissHomeTask}
						>
							<ArtistIcon name="close" size={18} />
						</button>
					</div>
				) : null}
				{snapshot === null && error === null ? <HomeSkeleton /> : null}
				{snapshot !== null ? (
					<DiscoverContent
						snapshot={snapshot}
						onUnavailable={() => setNotice(t("discovery.playbackBlocked.message"))}
						onRefresh={refresh}
					/>
				) : null}
			</section>

			<section role="tabpanel" hidden={tab !== "inspiration"} aria-label={t("discovery.tabs.inspiration")}>
				{inspirations === null && error === null ? (
					<InspirationsSkeleton />
				) : (
					<InspirationsContent inspirations={inspirations ?? []} />
				)}
			</section>

			<section role="tabpanel" hidden={tab !== "catalog"} aria-label={t("discovery.tabs.catalog")}>
				<SearchExperience repository={repository} />
			</section>
		</div>
	);
}

function DiscoverContent({
	snapshot,
	onUnavailable,
	onRefresh,
}: Readonly<{
	snapshot: DiscoverySnapshot;
	onUnavailable: () => void;
	onRefresh: () => void;
}>) {
	const { t } = useTranslation();
	const hasFeatured = snapshot.featured.length > 0;
	const hasCircles = snapshot.circles.length > 0;
	const hasTrending = snapshot.trendingFallback.length > 0;
	const hasRecent = snapshot.recent.length > 0;
	const hasWorks = snapshot.works.length > 0;
	const hasContent =
		hasFeatured || hasCircles || hasTrending || hasRecent || hasWorks;

	if (!hasContent) {
		return (
			<EmptyState
				title={t("discovery.empty.discover")}
				message={t("discovery.empty.discoverMessage")}
				actionLabel={t("common.refresh")}
				onAction={onRefresh}
			/>
		);
	}

	return (
		<main className="home-feed">
			{hasFeatured ? (
				<section className="home-section" aria-labelledby="home-hottest-title">
					<HomeSectionHeader
						title={t("discovery.hottest.title")}
						subtitle={t("discovery.hottest.subtitle")}
						id="home-hottest-title"
					/>
					<ul className="home-scroller-h">
						{snapshot.featured.slice(0, 10).map((track, index) => (
							<li key={track.id}>
								<LeaderboardCard
									rank={index + 1}
									track={track}
									queue={snapshot.featured}
									onUnavailable={onUnavailable}
								/>
							</li>
						))}
					</ul>
				</section>
			) : null}

			{hasCircles || hasTrending ? (
				<section className="home-section" aria-labelledby="home-circles-title">
					<HomeSectionHeader
						title={t("discovery.circles.title")}
						subtitle={hasCircles ? t("discovery.circles.playlists") : t("discovery.circles.trending")}
						id="home-circles-title"
					/>
					<ul className="home-scroller-h">
						{hasCircles
							? snapshot.circles.map((circle, index) => (
									<li key={circle.id}>
										<HotCircleCard
											title={circle.name}
											subtitle={circle.description}
											paletteIndex={index}
											href={`/playlist/${encodeURIComponent(circle.playlistId)}` as Route}
										/>
									</li>
								))
							: snapshot.trendingFallback.map((track, index) => (
									<li key={track.id}>
										<HotCircleCard
											title={track.genre}
											subtitle={track.title}
											paletteIndex={index}
											onUnavailable={onUnavailable}
										/>
									</li>
								))}
					</ul>
				</section>
			) : null}

			{hasRecent ? (
				<section
					className="home-section"
					aria-labelledby="home-last-listened-title"
				>
					<HomeSectionHeader
						title={t("discovery.recent.title")}
						subtitle={t("discovery.recent.subtitle")}
						id="home-last-listened-title"
						href="/recent"
						moreLabel={t("discovery.recent.more")}
					/>
					<ol className="home-last-listened-list">
						{snapshot.recent.slice(0, 3).map((track) => (
							<li key={track.id}>
								<LastListenedRow
									track={track}
									queue={snapshot.recent}
									onUnavailable={onUnavailable}
								/>
							</li>
						))}
					</ol>
				</section>
			) : null}

			{hasWorks ? (
				<section className="home-section" aria-labelledby="home-works-title">
					<HomeSectionHeader title={t("discovery.works.title")} id="home-works-title" />
					<ul className="home-your-works-scroller">
						{snapshot.works.map((track) => (
							<li key={track.id}>
								<YourWorkCard
									track={track}
									queue={snapshot.works}
									onUnavailable={onUnavailable}
								/>
							</li>
						))}
					</ul>
				</section>
			) : null}
		</main>
	);
}

function HomeSectionHeader({
	title,
	subtitle,
	id,
	href,
	moreLabel = "查看更多",
}: Readonly<{
	title: string;
	subtitle?: string;
	id?: string;
	href?: LinkProps<string>["href"];
	moreLabel?: string;
}>) {
	return (
		<div
			className={
				href === undefined
					? "home-section-header"
					: "home-section-header home-section-header--row"
			}
		>
			<div>
				<h2 id={id}>{title}</h2>
				{subtitle === undefined ? null : <p>{subtitle}</p>}
			</div>
			{href === undefined ? null : (
				<Link
					href={href}
					className="home-section-header__more"
					aria-label={moreLabel}
				>
					<ArtistIcon name="chevron" size={20} />
				</Link>
			)}
		</div>
	);
}

function LeaderboardCard({
	rank,
	track,
	queue,
	onUnavailable,
}: Readonly<{
	rank: number;
	track: DiscoveryTrack;
	queue: readonly DiscoveryTrack[];
	onUnavailable: () => void;
}>) {
	const href = playbackHref(track, queue);
	const tone = TONE_CLASS[track.artwork];
	const content = (
		<article className="home-leaderboard-card">
			<span className="home-leaderboard-card__rank">{rank}</span>
			<span className="home-leaderboard-card__body">
				<span className="home-leaderboard-card__cover">
					{track.artworkURL ? (
						// biome-ignore lint/performance/noImgElement: approved remote artwork is already CDN-sized and must remain compatible with dynamic origins.
						<img
							className="home-leaderboard-card__cover-bg"
							src={track.artworkURL}
							alt=""
						/>
					) : (
						<span className={`home-leaderboard-card__cover-bg ${tone}`} />
					)}
					<span className="home-leaderboard-card__overlay" />
					<span className="home-leaderboard-card__caption">
						<strong>{track.title}</strong>
						<span>{track.artist}</span>
					</span>
				</span>
				<span className={`home-leaderboard-card__lyric ${tone}`}>
					<span>{`“${track.description}”`}</span>
				</span>
			</span>
		</article>
	);
	if (href !== null) {
		return (
			<Link
				className="home-leaderboard-card-link"
				href={href}
				aria-label={`播放 ${track.title}`}
			>
				{content}
			</Link>
		);
	}
	return (
		<button
			type="button"
			className="home-leaderboard-card-link"
			onClick={onUnavailable}
			aria-label={`${track.title} 暂不可播放`}
		>
			{content}
		</button>
	);
}

function HotCircleCard({
	title,
	subtitle,
	paletteIndex,
	href,
	onUnavailable,
}: Readonly<{
	title: string;
	subtitle: string;
	paletteIndex: number;
	href?: LinkProps<string>["href"];
	onUnavailable?: () => void;
}>) {
	const content = (
		<article className="home-hot-circle-card">
			<span
				className={`home-hot-circle-card__bg ${paletteClass(paletteIndex)}`}
			/>
			<span className="home-hot-circle-card__shade" />
			<span className="home-hot-circle-card__play">
				<PlayCircleFilledGlyph size={28} />
			</span>
			<span className="home-hot-circle-card__caption">
				<strong>{title}</strong>
				<span>{subtitle}</span>
			</span>
		</article>
	);
	if (href !== undefined) {
		return (
			<Link
				className="home-hot-circle-card-link"
				href={href}
				aria-label={`${title} ${subtitle}`}
			>
				{content}
			</Link>
		);
	}
	return (
		<button
			type="button"
			className="home-hot-circle-card-link"
			onClick={onUnavailable}
			aria-label={`${title} ${subtitle}`}
		>
			{content}
		</button>
	);
}

function LastListenedRow({
	track,
	queue,
	onUnavailable,
}: Readonly<{
	track: DiscoveryTrack;
	queue: readonly DiscoveryTrack[];
	onUnavailable: () => void;
}>) {
	const href = playbackHref(track, queue);
	const subLabel =
		track.genre.length > 0 ? `${track.artist} · ${track.genre}` : track.artist;
	const content = (
		<article className="home-last-listened-row">
			<ArtworkThumbnail
				{...(track.artworkURL === undefined ? {} : { src: track.artworkURL })}
				size={56}
				alt={`${track.title} 封面`}
			/>
			<span className="home-last-listened-row__copy">
				<strong>{track.title}</strong>
				<span>{subLabel}</span>
			</span>
			<span className="home-last-listened-row__play" aria-hidden="true">
				<PlayCircleGlyph size={24} />
			</span>
		</article>
	);
	if (href !== null) {
		return (
			<Link
				className="home-last-listened-row-link"
				href={href}
				aria-label={`播放 ${track.title}`}
			>
				{content}
			</Link>
		);
	}
	return (
		<button
			type="button"
			className="home-last-listened-row-link"
			onClick={onUnavailable}
			aria-label={`${track.title} 暂不可播放`}
		>
			{content}
		</button>
	);
}

function YourWorkCard({
	track,
	queue,
	onUnavailable,
}: Readonly<{
	track: DiscoveryTrack;
	queue: readonly DiscoveryTrack[];
	onUnavailable: () => void;
}>) {
	const href = playbackHref(track, queue);
	const tone = TONE_CLASS[track.artwork];
	const content = (
		<article className="home-your-work-card">
			<span className={`home-your-work-card__bg ${tone}`} />
			<span className="home-your-work-card__shade" />
			<span className="home-your-work-card__title">{track.title}</span>
		</article>
	);
	if (href !== null) {
		return (
			<Link
				className="home-your-work-card-link"
				href={href}
				aria-label={`播放 ${track.title}`}
			>
				{content}
			</Link>
		);
	}
	return (
		<button
			type="button"
			className="home-your-work-card-link"
			onClick={onUnavailable}
			aria-label={`${track.title} 暂不可播放`}
		>
			{content}
		</button>
	);
}

function HomeSkeleton() {
	return (
		<div className="home-skeleton" role="status" aria-label="正在加载发现内容">
			<div className="home-skeleton__section">
				<div className="home-skeleton__h">
					<span />
					<span />
				</div>
				<div className="home-skeleton__row">
					<div
						className="home-skeleton__block"
						style={{ width: 280, height: 390 }}
					/>
					<div
						className="home-skeleton__block"
						style={{ width: 280, height: 390 }}
					/>
				</div>
			</div>
			<div className="home-skeleton__section">
				<div className="home-skeleton__h">
					<span />
					<span />
				</div>
				<div className="home-skeleton__row">
					<div
						className="home-skeleton__block"
						style={{ width: 180, height: 190, borderRadius: 28 }}
					/>
					<div
						className="home-skeleton__block"
						style={{ width: 180, height: 190, borderRadius: 28 }}
					/>
				</div>
			</div>
			<div className="home-skeleton__section">
				<div className="home-skeleton__h">
					<span />
					<span />
				</div>
				<div
					style={{
						display: "grid",
						gap: 12,
						padding: "0 var(--ds-space-lg)",
					}}
				>
					<div
						className="home-skeleton__block"
						style={{ height: 88, borderRadius: 20 }}
					/>
					<div
						className="home-skeleton__block"
						style={{ height: 88, borderRadius: 20 }}
					/>
				</div>
			</div>
		</div>
	);
}

function HeartGlyph({
	filled = false,
	size = 12,
}: Readonly<{ filled?: boolean; size?: number }>) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			aria-hidden="true"
			fill={filled ? "currentColor" : "none"}
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
		</svg>
	);
}

/**
 * Formats a like count the way iOS `InspirationsView.formatCount` does:
 * `>=10000` → `X.Xw`, `>=1000` → `X.Xk`, otherwise the plain number.
 */
function formatLikeCount(count: number): string {
	if (count >= 10000) {
		const value = count / 10000;
		return `${value.toFixed(1)}w`;
	}
	if (count >= 1000) {
		const value = count / 1000;
		return `${value.toFixed(1)}k`;
	}
	return `${count}`;
}

function InspirationsContent({
	inspirations,
}: Readonly<{ inspirations: readonly Inspiration[] }>) {
	if (inspirations.length === 0) {
		return (
			<EmptyState
				title="暂时没有灵感"
				message="稍后刷新重试。"
				symbol={<ArtistIcon name="sparkle" size={28} />}
			/>
		);
	}
	return (
		<main className="home-inspirations-feed" aria-label="灵感瀑布流">
			<ul className="home-inspirations-waterfall">
				{inspirations.map((item, index) => (
				<li key={item.id} style={{ breakInside: "avoid" }}>
					<InspirationThreadCard item={item} rank={index + 1} />
				</li>
			))}
			</ul>
		</main>
	);
}

function InspirationThreadCard({
	item,
	rank,
}: Readonly<{ item: Inspiration; rank: number }>) {
	const href = `/inspirations/${encodeURIComponent(item.id)}` as LinkProps<string>["href"];
	const authorName = item.author?.name ?? "匿名";
	const avatarInitial = authorName.trim().charAt(0) || "?";
	const likeCount = item.likeCount ?? 0;
	const isLiked = item.isLiked ?? false;
	return (
		<Link
			href={href}
			className="home-inspiration-thread"
			aria-label={`打开灵感 ${item.title}`}
			data-rank={rank}
		>
			<span
				className={`home-inspiration-thread__media ${TONE_CLASS[item.artwork]}`}
			>
				{item.mediaURL !== undefined ? (
					// biome-ignore lint/performance/noImgElement: discovery feed artwork with dynamic styling; next/image not feasible on static export
					<img
						className="home-inspiration-thread__img"
						src={item.mediaURL}
						alt={`${item.title} 封面`}
						loading="lazy"
						decoding="async"
					/>
				) : null}
			</span>
			<span className="home-inspiration-thread__content">
				<span className="home-inspiration-thread__title">{item.title}</span>
				<span className="home-inspiration-thread__meta">
					<span className="home-inspiration-thread__avatar" aria-hidden="true">
						{avatarInitial}
					</span>
					<span className="home-inspiration-thread__author">{authorName}</span>
					<span className="home-inspiration-thread__spacer" />
					<span
						className={`home-inspiration-thread__like${
							isLiked ? " home-inspiration-thread__like--on" : ""
						}`}
					>
						<HeartGlyph filled={isLiked} size={12} />
						<span>{formatLikeCount(likeCount)}</span>
					</span>
				</span>
			</span>
		</Link>
	);
}

function InspirationsSkeleton() {
	return (
		<div
			className="home-inspirations-skeleton"
			role="status"
			aria-label="正在加载灵感"
		>
			<ul className="home-inspirations-waterfall" aria-hidden="true">
				{INSPIRATION_SKELETONS.map((item) => (
					<li
						key={item.id}
						style={{ breakInside: "avoid" }}
					>
						<span className="home-inspiration-thread home-inspiration-thread--skeleton">
							<Skeleton
								shape="rectangle"
								width="100%"
								height={item.height}
							/>
							<span className="home-inspiration-thread__content">
								<Skeleton rows={2} />
							</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
